import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { toggleSwitch } from '../../reducers/switch';
import { useDispatch, useSelector } from 'react-redux';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import AddIcon from '../../assets/icons/add-icon.svg?react';
import Button from '../../components/Button';
import FilterClearIcon from '../../assets/icons/filter-clear-icon.svg?react';
import FilterIcon from '../../assets/icons/filter-icon.svg?react';
// import DownloadIcon from '../../assets/icons/download-icon.svg?react';

import { useButtonSizeLarge } from '../../utils/buttonSizes';
import SearchInput from '../../components/SearchInput';
import Select from '../../components/Select';
import DropDown from '../../components/DropDown';
import moment from 'moment';
import ModalComponent from '../../components/ModalComponent';
import { toggleModal } from '../../reducers/modalToggle';
import { useFormik } from 'formik';
import { getAddDocumentSchema } from './schema';
import AddDocumentModal from '../../components/AddDocumentModal';
import EditDocumentModal from '../../components/EditDocumentModal';
import { toast } from 'react-toastify';
import { useApiClients } from '../../client/api/useApiClient';
import {
  Attachments,
  DeleteDocumentLibraryResponseDto,
  DocumentLibraryApiDocumentLibraryControllerGetAllDocumentsRequest,
  DocumentLibraryControllerAddDocumentDocumentTypeEnum,
  DocumentLibraryControllerGetAllDocumentsDocumentTypeEnum,
  Documents,
  GetDocumentLibraryResponseDto,
  LoginResponseData,
} from '@EHSNavigator/backend';
import Loader from '../../components/Loader';
import NoItemsFound from '../../components/NoItemsFound';
import { useNavigate } from 'react-router';
import Pagination from '../../components/CustomPagination';
import { useDebounce, useLocalStorage } from '@uidotdev/usehooks';
import { queryClient } from '../../main';
import DeleteCustomQuestionModal from '../../components/DeleteCustomQuestionModal';
import { useMutation } from '@tanstack/react-query';
import { getBaseName } from '../../utils/timeFomat';
import AttachmentPreviewModal from '../../components/AttachmentPreviewModal';

interface DocumentLibraryProps {
  otherClasses?: string;
  data: GetDocumentLibraryResponseDto;
  noDataMessage: string;
  isLoading: boolean;
  params: DocumentLibraryApiDocumentLibraryControllerGetAllDocumentsRequest;
}

interface RootState {
  modalToggle: {
    modalName: string;
  };
}
const DocumentLibrary: React.FC<DocumentLibraryProps> = ({
  otherClasses,
  data,
  noDataMessage,
  isLoading,
  params,
}) => {
  const documentLibraryClasses = clsx(otherClasses);
  const lg = useButtonSizeLarge();
  const { DocumentLibraryApi } = useApiClients();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const modalName = useSelector(
    (state: RootState) => state.modalToggle.modalName,
  );
  const [userInfo] = useLocalStorage<LoginResponseData>('userInfo');
  const [selectedAttachment, setSelectedAttachment] = useState<Attachments | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<Documents | null>(
    null,
  );
  const [state, setState] = useState({
    page: Number(params?.page),
    search: params?.search || '',
    documentType: params?.documentType || 'All',
    limit: params?.limit || 10,
  });

  const debouncedSearch = useDebounce(state?.search, 600);
  const prevFiltersRef = useRef({});
  const isFirstRun = useRef(true);

  useEffect(() => {
    const filters = {
      search: debouncedSearch,
      documentType: state.documentType,

      limit: state.limit,
    };
    let shouldResetPage = false;
    if (!isFirstRun.current) {
      const filtersChanged =
        JSON.stringify(filters) !== JSON.stringify(prevFiltersRef.current);
      if (filtersChanged) {
        shouldResetPage = true;
        setState((prev) => ({ ...prev, page: 1 }));
      }
    }
    const paramsSearch = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'limit' && Number(value) === 10) {
        return;
      }
      if (value && !(key === 'documentType' && value === 'All')) {
        paramsSearch.set(key, value.toString());
      }
    });
    if (state.page !== 1) {
      paramsSearch.set('page', state.page.toString());
    }
    if (shouldResetPage) {
      paramsSearch.delete('page');
      setState((prev) => ({ ...prev, page: 1 }));
    }
    prevFiltersRef.current = filters;
    if (!isFirstRun.current) {
      navigate(`/documents?${paramsSearch.toString()}`);
    }
    isFirstRun.current = false;
  }, [debouncedSearch, state.page, state.documentType, state.limit]);

  const addDocumentHandler = useFormik({
    initialValues: {
      tags: [],
      documentType:
        userInfo?.user?.role?.name === 'Employee'
          ? DocumentLibraryControllerAddDocumentDocumentTypeEnum.MyDocument
          : '',
      documents: [],
    },
    enableReinitialize: true,
    validationSchema: getAddDocumentSchema(),
    onSubmit: async (values) => {
      try {
        const res =
          await DocumentLibraryApi.documentLibraryControllerAddDocument({
            documentType:
              values?.documentType as DocumentLibraryControllerAddDocumentDocumentTypeEnum,
            tags: values?.tags,
            files: values?.documents,
          });

        queryClient.setQueryData(
          [
            'all-documents',
            state?.page,
            state?.search,
            state?.documentType,
            state?.limit,
          ],
          (oldData: any) => {
            const updatedInvestigationQuestions = {
              ...oldData,
              documents: [
                ...(res?.data?.documents || []),
                ...(oldData?.documents || []),
              ],
            };
            return updatedInvestigationQuestions;
          },
        );
        toast.success(res?.data?.message);
        addDocumentHandler.resetForm();
        dispatch(toggleModal(''));
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  const deleteQuestionMutation = useMutation<
    DeleteDocumentLibraryResponseDto,
    Error,
    any
  >({
    mutationFn: async ({ id }: { id: string }) => {
      const response =
        await DocumentLibraryApi.documentLibraryControllerDeleteDocument({
          id,
        });

      return response.data;
    },
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(
        [
          'all-documents',
          state?.page,
          state?.search,
          state?.documentType,
          state?.limit,
        ],
        (oldData: any) => {
          const updatedInvestigationQuestions = {
            ...oldData,
            documents: oldData?.documents?.filter(
              (node: any) => node?.id !== id,
            ),
          };

          return updatedInvestigationQuestions;
        },
      );
      toast.success(data?.message);
      setSelectedDocument(null);
      dispatch(toggleModal(''));
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });
  const updateQuery = (queryName: string, queryValue: string) => {
    const params = new URLSearchParams(window.location.search);
    if (queryName === 'page' && queryValue === '1') {
      params.delete('page');
    } else {
      params.set(queryName, queryValue);
    }

    navigate(`/documents?${params.toString()}`);
  };

  const handlePageChange = (newPage: number) => {
    setState((prev) => ({ ...prev, page: newPage }));
    updateQuery('page', String(newPage));
  };

  return (
    <div className={documentLibraryClasses} data-testid="document-library">
      <div className="flex w-full flex-col items-stretch justify-between gap-15 sm:flex-row sm:items-center lg:gap-6">
        <div>
          <div className="flex items-center gap-2.5">
            <button
              className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
              onClick={() => dispatch(toggleSwitch())}
            >
              <ToggleIcon />
            </button>
            <h1 className="h3">Documents Library</h1>
          </div>
          <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 text-lg leading-5 font-medium lg:block lg:text-xl lg:leading-6">
            Manage all documents in your company.
          </p>
        </div>
        <Button
          size={lg}
          onClick={() => dispatch(toggleModal('add-document'))}
          otherClasses="relative whitespace-nowrap "
        >
          <AddIcon className="h-6 w-6 [&>path]:fill-current" /> Add New Document
        </Button>
      </div>
      <div className="mt-10">
        <SearchInput
          name="search"
          onChange={(e) =>
            setState((prev) => ({
              ...prev,
              search: e?.target?.value,
            }))
          }
          placeholder="Enter name, tags, author"
          value={state?.search}
          disabled={false}
          errorMessage=""
          otherClasses={{ container: 'w-full lg:mt-12 ' }}
        />
        <div className="global-scrollbar-style min-h-406 flex-1 overflow-auto px-0.5">
          <div className="flex w-full min-w-6xl flex-col">
            <div className="border-b-neutral-tones-text/50 relative mt-6 mb-12 flex flex-col gap-4 border-b pb-6 lg:flex-row lg:items-center lg:gap-9">
              <button
                onClick={() =>
                  setState((prev: any) => ({
                    ...prev,
                    search: '',
                    documentType: 'All',
                    limit: 10,
                  }))
                }
                className="font-inter-variable -tracking-form-input lg:-tracking-text-area text-brand-byzantine flex cursor-pointer items-center gap-2.5 object-fill text-base leading-5 font-bold lg:text-lg"
              >
                {state?.search !== '' ||
                  state?.documentType !== 'All' ||
                  state?.limit !== 10 ? (
                  <FilterClearIcon className="[&>path]:fill-brand-byzantine h-6 w-6 lg:h-9 lg:w-9" />
                ) : (
                  <FilterIcon className="h-6 w-6 lg:h-9 lg:w-9" />
                )}
                Filters
              </button>
              <div className="flex flex-1 items-center gap-6">
                <Select
                  filterSelect
                  list={Object.entries(
                    DocumentLibraryControllerGetAllDocumentsDocumentTypeEnum,
                  ).map(([, value]) => ({
                    label: value,
                    value: value,
                  }))}
                  value={state?.documentType}
                  placeholder="Document Type"
                  otherClasses={{ container: 'w-full max-w-48' }}
                  onSelect={(e) =>
                    setState((prev: any) => ({
                      ...prev,
                      documentType: e,
                    }))
                  }
                  errorMessage=""
                />
              </div>
            </div>
          </div>

          {isLoading ? (
            <div>
              <Loader
                variant="compact"
                otherClasses="bg-neutral-tones-background/50 z-20 backdrop-blur-[2px] lg:!w-[calc(100%-310px)] lg:right-0 lg:left-auto !fixed !top-0 left-0 h-dvh"
              />
            </div>
          ) : (
            <div className="w-full min-w-6xl">
              <div className="border-neutral-tones-text/50 grid grid-cols-12 gap-4 border-b pb-6">
                <h6 className="text-neutral-tones-text/50 font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                  Sr.
                </h6>
                <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                  Name
                </h6>
                <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                  Document Type
                </h6>
                <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                  Tags
                </h6>
                <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                  Author
                </h6>
                <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-3 font-bold lg:text-lg lg:leading-5">
                  Last Edit
                </h6>
              </div>
              {data?.documents?.length > 0 ? (
                <div>
                  {data?.documents?.map((node, index: number) => (
                    <div
                      key={index}
                      className="border-b-neutral-tones-text/50 grid grid-cols-12 items-center gap-4 border-b py-5"
                    >
                      <p className="text-neutral-tones-text/50 font-inter-variable lg:-tracking-form-input col-span-1 flex items-center text-sm leading-4 font-bold lg:text-base lg:leading-5 lg:font-normal">
                        {(state.page - 1) * state.limit + index + 1}
                      </p>
                      <p
                        title={node?.name}
                        className="text-neutral-tones-text font-inter-variable lg:-tracking-form-input col-span-2 truncate text-sm leading-4 font-normal whitespace-nowrap lg:text-base lg:leading-5"
                      >
                        {getBaseName(node?.name)?.name || '----'}
                      </p>
                      <p className="text-neutral-tones-text font-inter-variable lg:-tracking-form-input col-span-2 flex items-center text-sm leading-4 font-normal lg:text-base lg:leading-5">
                        {node?.documentType}
                      </p>
                      <p className="text-neutral-tones-text font-inter-variable lg:-tracking-form-input col-span-2 flex items-center text-sm leading-4 font-normal capitalize lg:text-base lg:leading-5">
                        {node?.tags?.join(', ')}
                      </p>
                      <p
                        title={node?.user?.fullName}
                        className="text-neutral-tones-text font-inter-variable lg:-tracking-form-input col-span-2 truncate text-sm leading-4 font-normal lg:text-base lg:leading-5"
                      >
                        {node?.user?.fullName}
                      </p>

                      <div className="col-span-3 flex items-center justify-between">
                        <p className="text-neutral-tones-text font-inter-variable lg:-tracking-form-input text-sm leading-4 font-normal lg:text-base lg:leading-5">
                          {moment(node?.updatedAt).format('MMMM DD, YYYY')}
                        </p>
                        <div className="flex items-center gap-2">
                          {(node?.documentType ===
                            DocumentLibraryControllerGetAllDocumentsDocumentTypeEnum.Organization
                            ? userInfo?.user?.role?.name !== 'Employee'
                            : true) && (
                              <DropDown
                                options={[
                                  {
                                    label: 'Edit',
                                    onClick: (node: any) => {
                                      setSelectedDocument(node);
                                      dispatch(toggleModal('edit-document'));
                                    },
                                    onOptionClickArgs: node,
                                    hidden: false,
                                  },
                                  {
                                    label: 'Delete',
                                    onClick: (node: any) => {
                                      setSelectedDocument(node);
                                      dispatch(toggleModal('delete-question'));
                                    },
                                    onOptionClickArgs: node,
                                    hidden: false,
                                  },
                                ]}
                                otherClasses="flex justify-center items-center"
                                additionalClasses={clsx(
                                  '!w-32 !top-auto !left-0 !right-auto !bottom-7',
                                )}
                              />
                            )}
                          <Button
                            otherClasses="!px-1.5 !py-1 !text-sm font-semibold "
                            variant="primary"
                            onClick={() => {
                              const { user, objectName, ...rest } = node;
                              const att = { ...rest, incidentId: "" }
                              setSelectedAttachment(att);
                              dispatch(toggleModal('attachment-preview-modal'));
                              // if (node?.url) {
                              //   window.open(node.url, '_blank');
                              // }
                            }}
                          >
                            {/* <DownloadIcon className="h-5 w-5 [&>g>path]:fill-current" /> */}
                            View Document
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <NoItemsFound message={noDataMessage} />
              )}
            </div>
          )}
        </div>
        {data?.documents?.length > 0 && (
          <Pagination
            currentPage={state.page}
            totalPages={data?.meta?.totalPages}
            totalItems={data?.meta?.total}
            limit={data?.meta?.limit}
            showingItems={data?.documents?.length}
            onPageChange={handlePageChange}
            onLimitChange={(e) =>
              setState((prev) => ({ ...prev, limit: e, page: 1 }))
            }
            otherClasses="mt-10"
          />
        )}
      </div>
      {(modalName === 'edit-document' ||
        modalName === 'add-document' ||
        modalName === 'attachment-preview-modal' ||
        modalName === 'delete-question') && (
          <ModalComponent
            otherClasses="px-4 sm:px-0 "
            additionalClass={clsx(
              'w-full max-w-573 rounded-2xl p-5 lg:px-9 lg:py-6 ',
            )}
            customClose={() => {
              setSelectedDocument(null);
              dispatch(toggleModal(''));
              addDocumentHandler.resetForm();
            }}
          >
            {modalName === 'add-document' && (
              <AddDocumentModal
                formHandler={addDocumentHandler}
                handleClose={() => {
                  addDocumentHandler.resetForm();
                  dispatch(toggleModal(''));
                }}
              />
            )}
            {modalName === 'edit-document' && (
              <EditDocumentModal
                selectedDocument={selectedDocument}
                state={state}
                handleClose={() => {
                  dispatch(toggleModal(''));
                  setSelectedDocument(null);
                }}
              />
            )}
            {modalName === 'delete-question' && (
              <DeleteCustomQuestionModal
                isDocument
                question={selectedDocument}
                handleClose={() => {
                  setSelectedDocument(null);
                  dispatch(toggleModal(''));
                }}
                isLoading={deleteQuestionMutation.isPending}
                handleDeleteQuestion={(e) => {
                  deleteQuestionMutation?.mutate({
                    id: e?.id,
                  });
                }}
              />
            )}

            {modalName === 'attachment-preview-modal' && (
              <AttachmentPreviewModal
                handleClose={() => {
                  dispatch(toggleModal(''));
                  setSelectedAttachment(null);
                }}
                attachment={selectedAttachment}
              />

            )}
          </ModalComponent>
        )}
    </div>
  );
};

export default DocumentLibrary;
